import type { PresenceStatus } from "@sagot/constants";
import { z } from "zod";

const teamCount = z.object({ team_id: z.string().nullable(), team: z.string().nullable(), count: z.number() });
const tierCount = z.object({ tier: z.string().nullable(), count: z.number() });
const heldChat = z.object({ conversation_id: z.string(), title: z.string(), held_s: z.number() });
const agent = z.object({
  member_id: z.string(),
  name: z.string(),
  status: z.enum(["online", "away", "busy", "offline"]),
  auto_away: z.boolean(),
  active_chats: z.number(),
  chat_limit: z.number(),
  longest_chat_s: z.number().nullable(),
  teams: z.array(z.string()),
  chats: z.array(heldChat),
});
const waitingChat = z.object({
  conversation_id: z.string().nullable(),
  title: z.string(),
  summary: z.string(),
  priority: z.string(),
  tier: z.string().nullable(),
  team: z.string().nullable(),
  waiting_s: z.number(),
  safety: z.boolean(),
});

/** One wallboard snapshot: the body of `GET /v1/wallboard` and the data of every `event: wallboard` (backend `WallboardOut`). */
export const snapshotSchema = z.object({
  generated_at: z.string(),
  refresh_s: z.number(),
  tiles: z.object({
    queue: z.object({ total: z.number(), by_team: z.array(teamCount), by_tier: z.array(tierCount) }),
    longest_wait_s: z.number().nullable(),
    priority_waiting: z.number(),
    agents_online: z.object({ online: z.number(), total: z.number() }),
    at_limit: z.number(),
    slas_at_risk: z.number(),
    safety_cases: z.number(),
  }),
  agents: z.array(agent),
  waiting: z.array(waitingChat),
});

export type WallboardSnapshot = z.infer<typeof snapshotSchema>;
export type WallboardAgent = WallboardSnapshot["agents"][number];
export type WaitingChat = WallboardSnapshot["waiting"][number];
export type HeldChat = WallboardAgent["chats"][number];
export type TileTone = "neutral" | "warning" | "danger";

/** A snapshot from the wire, or `null` when it is not one (a malformed event is skipped, never shown half-drawn). */
export function parseSnapshot(raw: unknown): WallboardSnapshot | null {
  const parsed = snapshotSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Seconds as a wall clock: `3:40`, `1:02:05`; an em dash for "no value". */
export function formatClock(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "–";
  const whole = Math.max(0, Math.floor(seconds));
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Whole seconds since `updatedAt` (epoch ms, the browser's own clock: the server's may differ); never negative. */
export function secondsAgo(updatedAt: number, now: number): number {
  return updatedAt <= 0 ? 0 : Math.max(0, Math.round((now - updatedAt) / 1000));
}

/** How full an agent is, 0 to 100 (a limit of zero reads as full). */
export function loadPercent(active: number, limit: number): number {
  if (limit <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round((active / limit) * 100)));
}

export function isAtLimit(agent: Pick<WallboardAgent, "active_chats" | "chat_limit">): boolean {
  return agent.active_chats >= agent.chat_limit;
}

const WAIT_WARNING_S = 120;
const WAIT_DANGER_S = 300;

export interface TileTones {
  longestWait: TileTone;
  priorityWaiting: TileTone;
  atLimit: TileTone;
  slasAtRisk: TileTone;
  safetyCases: TileTone;
}

/** Tone of each tile that can need attention: amber for a growing wait, priority or full agents; red for late SLAs and safety cases. */
export function tileTones(tiles: WallboardSnapshot["tiles"]): TileTones {
  const wait = tiles.longest_wait_s ?? 0;
  return {
    longestWait: wait >= WAIT_DANGER_S ? "danger" : wait >= WAIT_WARNING_S ? "warning" : "neutral",
    priorityWaiting: tiles.priority_waiting > 0 ? "warning" : "neutral",
    atLimit: tiles.at_limit > 0 ? "warning" : "neutral",
    slasAtRisk: tiles.slas_at_risk > 0 ? "danger" : "neutral",
    safetyCases: tiles.safety_cases > 0 ? "danger" : "neutral",
  };
}

export interface ReassignTarget {
  memberId: string;
  name: string;
  status: PresenceStatus;
  /** Why this person cannot take the chat now, or `null` when they can. */
  blocked: "limit" | "offline" | null;
}

/** Who a chat can move to: everyone but its holder, those who can take it first, then by name. */
export function reassignTargets(agents: readonly WallboardAgent[], holderId: string | null): ReassignTarget[] {
  return agents
    .filter((candidate) => candidate.member_id !== holderId)
    .map((candidate): ReassignTarget => ({
      memberId: candidate.member_id,
      name: candidate.name,
      status: candidate.status,
      blocked: isAtLimit(candidate) ? "limit" : candidate.status === "offline" ? "offline" : null,
    }))
    .sort((a, b) => Number(a.blocked !== null) - Number(b.blocked !== null) || a.name.localeCompare(b.name));
}

/** The queue's tiers for the tile, keeping the server's order (largest first) and naming the untiered. */
export function tierLabel(tier: string | null, none: string): string {
  return tier === null || tier === "" ? none : tier;
}
